import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StoryEngine, type PlayableNode } from '@tales/player-core';
import type { Choice, EndingNode, Episode } from '@tales/story-schema';
import { BuySheet, GiftToast, UnlockSheet } from '../components/Sheets';
import { setContext, track } from '../lib/analytics';
import { api, ApiError } from '../lib/api';
import { holdFrameUrl, mediaUrl } from '../lib/content';
import { isDebug, isInAppBrowser, queryParam } from '../lib/env';
import { useSession } from '../lib/session';
import { readJson, writeJson } from '../lib/storage';
import { ChoiceOverlay } from './ChoiceOverlay';
import { EndingCard } from './EndingCard';
import { VideoPool } from './VideoPool';

interface Props {
  seriesId: string;
  seriesTitle: string;
  episode: Episode;
  /** Credits needed to unlock this episode (0 = free). */
  price: number;
  episodeIndex: number;
  nextEpisode?: { id: string; title: string; price: number } | undefined;
  onHome: () => void;
  onPlayEpisode: (episodeId: string) => void;
}

type Phase = 'gate' | 'playing' | 'choosing' | 'ending';
type Sheet = null | { kind: 'unlock'; target: 'this' | 'next' } | { kind: 'buy'; target: 'this' | 'next' } | { kind: 'gift'; message: string; target: 'this' | 'next' };

const POOL_SIZE = 4;

export function Player({ seriesId, seriesTitle, episode, price, episodeIndex, nextEpisode, onHome, onPlayEpisode }: Props) {
  const session = useSession();
  const stageRef = useRef<HTMLDivElement>(null);
  const poolRef = useRef<VideoPool | null>(null);
  const engineRef = useRef<StoryEngine | null>(null);
  const [phase, setPhase] = useState<Phase>('gate');
  const [current, setCurrent] = useState<PlayableNode | null>(null);
  const [progress, setProgress] = useState(0);
  const [buffering, setBuffering] = useState(false);
  const [muted, setMuted] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [debug, setDebug] = useState('');
  const [sheet, setSheet] = useState<Sheet>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [seed, setSeed] = useState<Record<string, number | boolean> | null>(null);
  const [reached, setReached] = useState<string[]>(() => readJson<string[]>(`tales.endings.${seriesId}.${episode.id}`, []));
  const timing = useRef({ nodeStart: 0, endedAt: 0, choiceShown: 0, sessionStart: 0 });
  const activeSlotId = useRef<string | null>(null);

  const key = `${seriesId}/${episode.id}`;
  const unlocked = price === 0 || session.unlocks.has(key);
  const balance = session.user?.credits ?? 0;

  const allEndings = useMemo(
    () =>
      Object.values(episode.nodes)
        .filter((n): n is EndingNode => n.type === 'ending')
        .map((n) => ({ id: n.endingId, title: n.endingTitle })),
    [episode],
  );

  const startNode = episode.nodes[episode.start];
  const startPoster = startNode && startNode.type !== 'switch' ? mediaUrl(seriesId, episode.id, startNode.video?.poster) : undefined;

  // Series-scoped state from earlier episodes seeds this one.
  useEffect(() => {
    let cancelled = false;
    const hasSeriesVars = Object.values(episode.variables).some((v) => v.scope === 'series');
    if (!hasSeriesVars || !session.online || episodeIndex === 0) {
      setSeed({});
      return;
    }
    api.seriesState(seriesId).then(
      (r) => {
        if (!cancelled) setSeed(r.state?.vars ?? {});
      },
      () => {
        if (!cancelled) setSeed({});
      },
    );
    return () => {
      cancelled = true;
    };
  }, [episode, seriesId, session.online, episodeIndex]);

  // ----- pool lifecycle -----
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const pool = new VideoPool(stage, POOL_SIZE);
    poolRef.current = pool;
    setContext({ series: seriesId, episode: episode.id });
    track({ type: 'session_start', series: seriesId, episode: episode.id, referrer: document.referrer, query: location.search, inApp: isInAppBrowser });
    return () => {
      pool.destroy();
      poolRef.current = null;
    };
  }, [seriesId, episode.id]);

  const srcFor = useCallback(
    (nodeId: string) => {
      const n = episode.nodes[nodeId];
      if (!n || n.type === 'switch' || !n.video) return undefined;
      return { src: mediaUrl(seriesId, episode.id, n.video.src)!, poster: mediaUrl(seriesId, episode.id, n.video.poster) };
    },
    [episode, seriesId],
  );

  const prefetch = useCallback(
    (node: PlayableNode) => {
      const pool = poolRef.current;
      if (!pool) return;
      const keep = new Set<string>([node.id, ...node.nextCandidates]);
      pool.prune(keep);
      const def = node.node.type === 'video' ? node.node.defaultChoice : undefined;
      const defTarget = def ? node.choices.find((c) => c.id === def) : undefined;
      const ordered = [...node.nextCandidates];
      if (defTarget && typeof defTarget.to === 'string') {
        const i = ordered.indexOf(defTarget.to);
        if (i > 0) ordered.unshift(...ordered.splice(i, 1));
      }
      for (const id of ordered.slice(0, POOL_SIZE - 1)) {
        const m = srcFor(id);
        if (m) pool.assign(id, m.src, m.poster, keep);
      }
    },
    [srcFor],
  );

  const saveProgress = useCallback(
    (endingId?: string) => {
      const e = engineRef.current;
      if (!e || !session.online) return;
      const st = e.getState();
      const seriesVars = e.carryOver();
      api
        .saveProgress({
          seriesId,
          episodeId: episode.id,
          nodeId: st.nodeId,
          vars: st.vars,
          history: st.history,
          ...(endingId ? { endingId } : {}),
          ...(Object.keys(seriesVars).length ? { seriesVars } : {}),
        })
        .then(() => {
          session.upsertProgress({
            seriesId,
            episodeId: episode.id,
            nodeId: st.nodeId,
            endings: endingId ? Array.from(new Set([...reached, endingId])) : reached,
            updatedAt: new Date().toISOString(),
            completedAt: endingId ? new Date().toISOString() : null,
            steps: st.history.length,
          });
        })
        .catch(() => undefined);
    },
    [episode.id, reached, seriesId, session],
  );

  const showNode = useCallback(
    (node: PlayableNode, fromGesture: boolean) => {
      const pool = poolRef.current;
      if (!pool) return;
      const m = srcFor(node.id);
      if (!m) {
        track({ type: 'error', where: node.id, message: 'node has no video' });
        return;
      }
      const slot = pool.assign(node.id, m.src, m.poster, new Set([node.id]));
      if (!slot) return;
      const prev = activeSlotId.current;
      const el = slot.el;
      el.currentTime = 0;
      el.classList.add('active');
      const onPlaying = () => {
        el.removeEventListener('playing', onPlaying);
        const gap = timing.current.endedAt ? Math.round(performance.now() - timing.current.endedAt) : undefined;
        for (const s of pool.slots) if (s.el !== el && s.el.classList.contains('active')) s.el.classList.remove('active');
        if (prev && prev !== node.id) {
          const ps = pool.slotFor(prev);
          if (ps) pool.release(ps);
        }
        setBuffering(false);
        track(gap !== undefined ? { type: 'node_start', node: node.id, gapMs: gap } : { type: 'node_start', node: node.id });
      };
      el.addEventListener('playing', onPlaying);
      activeSlotId.current = node.id;
      timing.current.nodeStart = performance.now();
      setCurrent(node);
      setPhase('playing');
      setBuffering(true);
      void el.play().catch((err: unknown) => {
        setBuffering(false);
        track({ type: 'error', where: `play:${node.id}`, message: err instanceof Error ? err.message : String(err) });
        if (!fromGesture) setToast('Tap to resume');
      });
      prefetch(node);
      if (node.node.type !== 'ending') saveProgress();
    },
    [prefetch, saveProgress, srcFor],
  );

  // ----- playback events on the active element -----
  useEffect(() => {
    const pool = poolRef.current;
    if (!pool || !current) return;
    const slot = pool.slotFor(current.id);
    if (!slot) return;
    const el = slot.el;
    const node = current.node;
    let overlayShown = false;
    const leadIn = node.type === 'video' ? node.choiceLeadIn : 0;
    const showChoices = () => {
      overlayShown = true;
      setPhase('choosing');
      timing.current.choiceShown = performance.now();
      track({ type: 'choice_shown', node: current.id, options: current.choices.map((c) => c.id), ...(node.type === 'video' && node.timer !== undefined ? { timer: node.timer } : {}) });
    };
    const onTime = () => {
      if (el.duration) setProgress(el.currentTime / el.duration);
      if (!overlayShown && node.type === 'video' && node.choices && leadIn > 0 && el.duration && el.duration - el.currentTime <= leadIn) showChoices();
    };
    const onEnded = () => {
      timing.current.endedAt = performance.now();
      track({ type: 'node_complete', node: current.id, watchedMs: Math.round(performance.now() - timing.current.nodeStart) });
      if (node.type === 'ending') {
        setPhase('ending');
        const e = engineRef.current;
        const st = e?.getState();
        const next = Array.from(new Set([...reached, node.endingId]));
        setReached(next);
        writeJson(`tales.endings.${seriesId}.${episode.id}`, next);
        track({ type: 'ending_reached', node: current.id, ending: node.endingId, pathLength: st?.history.length ?? 0, totalMs: Math.round(performance.now() - timing.current.sessionStart) });
        saveProgress(node.endingId);
        return;
      }
      if (node.choices) {
        if (!overlayShown) showChoices();
        return;
      }
      const e = engineRef.current;
      if (e) showNode(e.advance(), false);
    };
    const onWaiting = () => setBuffering(true);
    const onPlaying = () => setBuffering(false);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('ended', onEnded);
    el.addEventListener('waiting', onWaiting);
    el.addEventListener('playing', onPlaying);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('waiting', onWaiting);
      el.removeEventListener('playing', onPlaying);
    };
  }, [current, episode.id, reached, saveProgress, seriesId, showNode]);

  // ----- gestures -----
  const start = () => {
    if (!unlocked) {
      track({ type: 'unlock_gate_shown', series: seriesId, episode: episode.id, price });
      setSheet({ kind: 'unlock', target: 'this' });
      return;
    }
    const pool = poolRef.current;
    if (!pool) return;
    pool.unlock(true);
    setMuted(false);
    const startAt = queryParam('start');
    const engine = new StoryEngine(episode, { ...(startAt ? { startAt } : {}), seed: seed ?? {} });
    engineRef.current = engine;
    timing.current.sessionStart = performance.now();
    timing.current.endedAt = 0;
    const node = engine.current();
    track({ type: 'play_start', series: seriesId, episode: episode.id, node: node.id });
    showNode(node, true);
  };

  const choose = useCallback(
    (choiceId: string, timedOut: boolean) => {
      const engine = engineRef.current;
      if (!engine || !current) return;
      track({ type: 'choice_made', node: current.id, choice: choiceId, decisionMs: Math.round(performance.now() - timing.current.choiceShown), timedOut });
      timing.current.endedAt = performance.now();
      const next = engine.choose(choiceId);
      showNode(next, !timedOut);
    },
    [current, showNode],
  );

  const replay = () => {
    const engine = engineRef.current;
    if (!engine) return;
    track({ type: 'replay', from: current?.id ?? '' });
    timing.current.sessionStart = performance.now();
    timing.current.endedAt = 0;
    showNode(engine.restart(), true);
  };

  const share = async () => {
    const url = `${location.origin}${location.pathname}`;
    const data = { title: `${seriesTitle} — ${episode.title}`, text: 'You decide what happens next.', url };
    try {
      if (navigator.share) {
        await navigator.share(data);
        track({ type: 'share', method: 'native' });
      } else {
        await navigator.clipboard.writeText(url);
        setToast('Link copied');
        track({ type: 'share', method: 'clipboard' });
      }
    } catch {
      /* cancelled */
    }
  };

  // ----- credits flow -----
  const targetEpisode = (target: 'this' | 'next') => (target === 'this' ? { id: episode.id, title: episode.title, price } : nextEpisode!);

  const doUnlock = async (target: 'this' | 'next') => {
    const t = targetEpisode(target);
    setBusy('unlock');
    try {
      const r = await api.unlock(seriesId, t.id);
      session.setCredits(r.balance);
      session.addUnlock(seriesId, t.id);
      track({ type: 'unlock', series: seriesId, episode: t.id, price: r.charged });
      setSheet(null);
      if (target === 'next') onPlayEpisode(t.id);
      else setToast('Unlocked');
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        session.setCredits(Number(err.body['balance'] ?? balance));
        track({ type: 'buy_sheet_shown', series: seriesId, episode: t.id, context: 'insufficient' });
        setSheet({ kind: 'buy', target });
      } else setToast('Could not unlock. Check your connection.');
    } finally {
      setBusy(null);
    }
  };

  const doIntent = async (sku: string, target: 'this' | 'next') => {
    const t = targetEpisode(target);
    setBusy(sku);
    track({ type: 'purchase_intent', series: seriesId, episode: t.id, sku });
    try {
      const r = await api.purchaseIntent(sku, seriesId, t.id, 'unlock_gate');
      session.setCredits(r.balance);
      setSheet({ kind: 'gift', message: r.message, target });
    } catch {
      setToast('Something went wrong. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const toggleMute = () => {
    const pool = poolRef.current;
    if (!pool) return;
    const next = !muted;
    pool.setMuted(next);
    setMuted(next);
  };

  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 1800);
    return () => window.clearTimeout(id);
  }, [toast]);

  useEffect(() => {
    if (!isDebug) return;
    const id = window.setInterval(() => {
      const e = engineRef.current;
      const pool = poolRef.current;
      setDebug(
        [
          `node ${current?.id ?? '-'} phase ${phase} online ${session.online}`,
          `vars ${JSON.stringify(e?.getState().vars ?? seed ?? {})}`,
          ...(pool?.slots.map((s) => `slot${s.el.dataset['slot']} ${s.nodeId ?? '-'} rs${s.el.readyState} ${s.el.classList.contains('active') ? '*' : ''}`) ?? []),
        ].join('\n'),
      );
    }, 500);
    return () => window.clearInterval(id);
  }, [current, phase, seed, session.online]);

  const engineState = engineRef.current?.getState();
  const pathLabels: string[] = [];
  if (engineState) {
    for (const h of engineState.history) {
      if (!h.choiceId) continue;
      const n = episode.nodes[h.nodeId];
      const c = n?.type === 'video' ? n.choices?.find((x: Choice) => x.id === h.choiceId) : undefined;
      if (c) pathLabels.push(c.text);
    }
  }

  const videoNode = current?.node.type === 'video' ? current.node : undefined;
  const nextUnlocked = nextEpisode ? nextEpisode.price === 0 || session.unlocks.has(`${seriesId}/${nextEpisode.id}`) : false;

  return (
    <div className="stage-wrap">
      <div className="stage" ref={stageRef}>
        {phase === 'choosing' && current ? (
          <img className="hold show" src={holdFrameUrl(seriesId, episode.id, current.id)} alt="" onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')} />
        ) : null}

        {phase !== 'gate' ? (
          <div className="topbar">
            <button className="iconbtn" onClick={onHome} aria-label="Back">
              ‹
            </button>
            <div className="progress">
              <div style={{ width: `${Math.round(progress * 100)}%` }} />
            </div>
            {session.user ? <span className="wallet">{session.user.credits}</span> : null}
            <button className="iconbtn" onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'}>
              {muted ? '🔇' : '🔊'}
            </button>
          </div>
        ) : null}

        {buffering && phase === 'playing' ? <div className="spinner" /> : null}

        {phase === 'gate' ? (
          <div className="gate">
            {startPoster ? <img className="poster" src={startPoster} alt="" /> : null}
            <div className="label">
              {seriesTitle} · Episode {episodeIndex + 1}
            </div>
            <h1>{episode.title}</h1>
            {episode.synopsis ? <p>{episode.synopsis}</p> : null}
            <button className="cta" onClick={start} disabled={seed === null && session.online}>
              {unlocked ? '▶ Tap to start — sound on' : `🔒 Unlock · ${price} credits`}
            </button>
            <p style={{ marginTop: 12, fontSize: 12.5 }}>
              {allEndings.length} endings · you choose what happens · {reached.length ? `${reached.length} found so far` : 'about 1 minute'}
            </p>
            {session.ready && !session.online && price > 0 ? <p className="offline">Offline: paid episodes need a connection.</p> : null}
          </div>
        ) : null}

        {phase === 'choosing' && current && videoNode?.choices ? (
          <ChoiceOverlay key={current.id} choices={current.choices} timer={videoNode.timer} defaultChoiceId={videoNode.defaultChoice} onChoose={choose} />
        ) : null}

        {phase === 'ending' && current?.node.type === 'ending' ? (
          <EndingCard
            node={current.node}
            allEndings={allEndings}
            reached={reached}
            path={pathLabels}
            onReplay={replay}
            onShare={() => void share()}
            onHome={onHome}
            next={
              nextEpisode
                ? {
                    title: nextEpisode.title,
                    price: nextEpisode.price,
                    unlocked: nextUnlocked,
                    onPlay: () => {
                      if (nextUnlocked) onPlayEpisode(nextEpisode.id);
                      else {
                        track({ type: 'unlock_gate_shown', series: seriesId, episode: nextEpisode.id, price: nextEpisode.price });
                        setSheet({ kind: 'unlock', target: 'next' });
                      }
                    },
                  }
                : undefined
            }
          />
        ) : null}

        {sheet?.kind === 'unlock' ? (
          <UnlockSheet
            title={targetEpisode(sheet.target).title}
            price={targetEpisode(sheet.target).price}
            balance={balance}
            busy={busy === 'unlock'}
            onUnlock={() => void doUnlock(sheet.target)}
            onGetCredits={() => {
              track({ type: 'buy_sheet_shown', series: seriesId, episode: targetEpisode(sheet.target).id, context: 'unlock_sheet' });
              setSheet({ kind: 'buy', target: sheet.target });
            }}
            onClose={() => setSheet(null)}
          />
        ) : null}
        {sheet?.kind === 'buy' ? <BuySheet packs={session.packs} busy={busy} onPick={(sku) => void doIntent(sku, sheet.target)} onClose={() => setSheet(null)} /> : null}
        {sheet?.kind === 'gift' ? (
          <GiftToast
            message={sheet.message}
            onDone={() => {
              const t = sheet.target;
              setSheet(null);
              void doUnlock(t);
            }}
          />
        ) : null}

        {toast ? <div className="toast">{toast}</div> : null}
        {isInAppBrowser && phase === 'gate' ? (
          <div className="banner">
            <span>For the best experience open this in your browser.</span>
            <button className="iconbtn" onClick={() => void share()} aria-label="Share">
              ↗
            </button>
          </div>
        ) : null}
        {isDebug ? <pre className="debug">{debug}</pre> : null}
      </div>
    </div>
  );
}
